import { isUploadTerminalFailure, isUploadTerminalRefusal } from "./uploadFacadeErrors";
import { UPLOAD_DRAIN_POLL_INTERVAL_MS } from "./periodicWake";

/**
 * The retry decision is deliberately data-only.  The drain owns persistence and
 * scheduling; this module only answers whether an item may be attempted again.
 */
export type UploadFailureKind = "transient" | "refresh" | "timeout" | "cancelled" | "terminal" | "auth" | "unknown";

export interface UploadFailureClassification {
	kind: UploadFailureKind;
	retryable: boolean;
	/** A terminal classification is item-scoped; it must not stop unrelated work. */
	terminal: boolean;
	/**
	 * R-0: the failure has repeated enough that it should be PACED, not parked. A lane that sees
	 * this schedules the slow (minutes) backoff tier instead of the fast one. It is never a verdict:
	 * `terminal` alone decides whether an item leaves the automatically scheduled pool.
	 */
	slowTier?: boolean;
	/**
	 * R-2 (owner directive 2026-08-27, "an auth failure is a pause, not a verdict"): this failure
	 * must consume NO retry budget — no attempt increment, no poison credit, no backoff-tier
	 * advance. It is the exact treatment R-0 gave offline sentinels, expressed on the
	 * classification so a lane does not have to re-derive it from `kind`.
	 *
	 * Offline stays a separate predicate (`isOfflineUploadFailure`) because it is a DEVICE fact the
	 * host answers, not something the error alone can prove; this flag is for the failures the
	 * classifier itself can name as budget-free.
	 */
	costsNoAttempt?: boolean;
}

/**
 * The attempt count at which an opaque ("unknown") failure stops being treated as a fresh
 * diagnostic and starts being paced on the slow tier.
 *
 * R-0 (owner directive 2026-08-26) changed what this constant DOES. It used to terminalize an
 * unknown failure once the diagnostic budget was spent — an attempt-count-derived terminal, exactly
 * the class of state this directive abolishes. It now only escalates the backoff tier: an unknown
 * failure retries forever, just more slowly, because "we could not classify it" is not evidence
 * that the upload can never succeed.
 */
export const MAX_UNKNOWN_DIAGNOSTIC_ATTEMPTS = 2;
export const DEFAULT_MAX_ACTIVE_ATTEMPTS = 4;
export const DEFAULT_RETRY_BASE_DELAY_MS = 1_000;
export const DEFAULT_RETRY_MAX_DELAY_MS = 30_000;
/** Durable marker appended by terminal writers whose typed status must survive reload. */
export const PERSISTED_TERMINAL_REJECTION_CODE = "UPLOAD_REJECTED";
/** Durable marker for a structured auth failure whose status/code fields cannot survive a string-only store. */
export const PERSISTED_AUTH_GATED_CODE = "AUTH_GATED";

/**
 * Client-minted ceiling on the SETTLING verdict `UPLOAD_OPERATION_IN_PROGRESS`.
 *
 * The server never sends this code. A lane mints it once it has watched the same durable item be
 * answered "an upload operation is in progress" for longer than any real completion or release can
 * plausibly take, which is positive evidence that the operation is wedged rather than settling.
 * Without it, `refresh` retries forever and a rotation can spin long enough to reach the server's
 * 120-minute anti-entropy repair window, where a same-idempotency replay of a rotation that never
 * finalized can be reported as an `already-complete` SUCCESS.
 */
export const UPLOAD_SETTLING_EXPIRED_CODE = "UPLOAD_SETTLING_EXPIRED";

/** Repeated failures use the same 30-second ceiling as every other upload retry. */
export const R0_SLOW_TIER_BASE_DELAY_MS = DEFAULT_RETRY_MAX_DELAY_MS;
export const R0_SLOW_TIER_MAX_DELAY_MS = DEFAULT_RETRY_MAX_DELAY_MS;
export const OFFLINE_RETRY_PROBE_MS = UPLOAD_DRAIN_POLL_INTERVAL_MS;

interface ErrorLike {
  name?: unknown;
  code?: unknown;
  status?: unknown;
  statusCode?: unknown;
  message?: unknown;
  error?: unknown;
  errorCode?: unknown;
  cause?: unknown;
  originalError?: unknown;
  response?: { status?: unknown };
  cancelled?: unknown;
}

function asErrorLike(error: unknown): ErrorLike {
  return typeof error === "object" && error !== null
    ? (error as ErrorLike)
    : {};
}

/**
 * The first candidate that is a REAL HTTP status, not merely the first that is an integer (LSP-19351).
 *
 * Every consumer asks the same question — "did a server answer?" — and each already range-checks the
 * result (`isAuthUploadFailure`, `isOfflineUploadFailure`) or compares it against specific codes
 * (`classifyUploadFailure`). Returning the first INTEGER therefore let an out-of-range value in an
 * EARLIER slot hide a real status in a later one: `status: 0` on a browser error object masks
 * `response.status: 503`, so the "a response came back, so the device demonstrably reached the
 * server" tier is skipped for a failure that provably reached it, and the verdict falls through to
 * the typed-marker tier — which can answer "offline". Since LSP-19345 an offline verdict also costs
 * no upload concurrency, so a misclassification here is not cosmetic.
 *
 * Scanning for the in-range value instead is behaviour-preserving everywhere else. An out-of-range
 * integer previously reached the callers as a number they had no rule for and now reaches them as
 * `undefined`, which those callers already treat identically — the sole exception being
 * `classifyUploadFailure`'s unguarded `status >= 500`, which no longer matches an integer of 600 or
 * more. Nothing mints one: HTTP statuses stop at 599, and `DOMException.code` — the only numeric
 * `code` a browser sets on these errors — tops out at 25.
 */
function getStatus(error: ErrorLike): number | undefined {
  const pending: unknown[] = [error];
  const seen = new Set<unknown>();
  while (pending.length > 0 && seen.size < 8) {
    const current = pending.shift();
    if (typeof current !== "object" || current === null || seen.has(current)) continue;
    seen.add(current);
    const details = current as ErrorLike;
    const candidates = [details.status, details.statusCode, details.code, details.response?.status];
    for (const candidate of candidates) {
      if (typeof candidate === "number" && Number.isInteger(candidate) && candidate >= 100 && candidate <= 599)
        return candidate;
    }
    pending.push(details.cause, details.originalError, details.error);
  }
  return undefined;
}

function getMarker(error: ErrorLike): string {
  return [error.name, error.code, error.message]
    .filter((value): value is string => typeof value === "string")
    .join(" ")
    .toUpperCase();
}

function hasExactCode(value: unknown, code: string): boolean {
	return typeof value === "string" && value.trim().toUpperCase() === code;
}

function hasTypedCode(error: ErrorLike, code: string): boolean {
	return (
		hasExactCode(error.name, code) ||
		hasExactCode(error.code, code) ||
		hasExactCode(error.error, code) ||
		hasExactCode(error.errorCode, code)
	);
}

function hasExactMessage(error: ErrorLike, message: string): boolean {
	return typeof error.message === "string" && error.message.trim().toUpperCase() === message;
}

/**
 * R-2's durable vocabulary, client side: the TYPED, enumerated reason codes that mean
 * "waiting for a credential we do not currently hold".
 *
 * All entries are code TOKENS, never prose. `AUTH_GATED` is the client-owned durable marker used
 * when a structured HTTP status must survive a string-only store; the others are wire codes. R-2
 * rule 4 abolishes status-shaped text matching
 * outright ("a durable verdict is a typed, enumerated reason code or it is no verdict at all"), so
 * this list is the only textual evidence an auth classification will ever accept. Typed wire codes
 * use the delimiter rule below; persisted messages use the stricter formatter/storage shapes.
 *
 * Why each one is auth-gated rather than terminal:
 *  - `UPLOAD_NOT_FOUND` / `NOT_FOUND`   the server's 404 is DOCUMENTED as identical for "absent id",
 *    "wrong tenant" and "no longer permitted", so it cannot
 *    prove absence. Lifecycle-terminal authorised refusals now use UPLOAD_TERMINAL,
 *    whose status/target details are checked separately; it is never an auth code.
 *  - `CAPABILITY_EXPIRED`               an expiry. A new capability is exactly what re-auth mints.
 *    (It also has no producer anywhere in `apps/contact-api/src` — verified repo-wide.)
 *  - `URL_REFRESH_AUTH_REJECTED`        an expiry by name: a refresh refused after a BOUNDED token
 *    renewal. Bounded means "we stopped asking", not "the credential can never come back".
 *  - `AUTH_TOKEN_STALE` / `AUTH_REVOKED` / `AUTH_WRONG_TENANT`   the vocabulary R-2 asks the server
 *    for. Read now so the client half is already in place when the API ships it.
 *  - `SESSION_LOST`                     a lost session is a pause, not a verdict (R-2 §Policy).
 *
 * Auth-shaped facts that DO earn a terminal — `ORGANIZATION_DELETED` and `SHARE_EXPIRED` — are
 * deliberately not here; they live in `smallFileLane`'s evidence set, and evidence is checked before
 * auth.
 */
export const AUTH_GATED_ERROR_CODES: readonly string[] = [
  PERSISTED_AUTH_GATED_CODE,
  "UPLOAD_NOT_FOUND",
  "NOT_FOUND",
  "CAPABILITY_EXPIRED",
  "URL_REFRESH_AUTH_REJECTED",
  "AUTH_TOKEN_STALE",
  "AUTH_REVOKED",
  "AUTH_WRONG_TENANT",
  "SESSION_LOST"
];

/** Preserve the diagnostic message while retaining the typed auth decision across string-only storage. */
export function formatAuthGatedUploadFailureReason(error: unknown): string {
  const details = asErrorLike(error);
  const message = typeof details.message === "string" && details.message ? details.message : String(error);
  const persistedMarker = ` [${PERSISTED_AUTH_GATED_CODE}]`;
  return message.endsWith(persistedMarker) ? message : `${message}${persistedMarker}`;
}

/**
 * Typed wire codes keep the evidence-marker delimiter rule: a code token may sit beside other
 * typed data, but never be a fragment of a longer identifier. The client-owned durable marker is
 * excluded because only the exact formatter-owned message suffix is authoritative.
 */
const AUTH_GATED_WIRE_CODES = AUTH_GATED_ERROR_CODES.filter((code) => code !== PERSISTED_AUTH_GATED_CODE);
const AUTH_GATED_CODE_PATTERNS = AUTH_GATED_WIRE_CODES.map((code) => new RegExp(`(?:^|[^A-Z0-9_-])${code}(?:[^A-Z0-9_-]|$)`));

function hasPersistedAuthGatedMessage(error: ErrorLike): boolean {
  if (typeof error.message !== "string") return false;
  if (error.message.endsWith(` [${PERSISTED_AUTH_GATED_CODE}]`)) return true;
  const message = error.message.trim().toUpperCase();
  if (AUTH_GATED_WIRE_CODES.some((code) => message === code || message.endsWith(`[${code}]`))) return true;
  const messageWithoutQuotedWireCodes = AUTH_GATED_WIRE_CODES.reduce(
    (candidate, code) => candidate.replaceAll(`[${code}]`, ""),
    message
  );
  return AUTH_GATED_CODE_PATTERNS.some((pattern) => pattern.test(messageWithoutQuotedWireCodes));
}

/**
 * The evidence a caller can supply about WHERE a failure came from and whether this surface holds a
 * credential at all. Mirrors `isOfflineUploadFailure`'s `offline` argument: the host's own verdict is
 * always stronger than anything read off the error.
 */
export interface UploadAuthFailureEvidence {
	/**
	 * The host's own answer to "do we currently hold a usable credential?" — normally
	 * `authGate.isAuthCurrent()`. `false` is decisive (we demonstrably hold nothing). `true` does not
	 * veto a real 401: the token we hold may simply be stale. It vetoes only the weakest tier, the
	 * code token read out of a message string.
	 */
	authCurrent?: boolean;
	/**
	 * Which host answered. `"s3"` is decisive in the NEGATIVE: a presigned-URL 403/404 is an EXPIRY,
	 * and `uploadChunk.ts`'s refresh lane is the authority on it — R-2 must not re-route those into
	 * an API auth verdict. `"api"` and `"unknown"` behave identically; the field exists so a caller
	 * that CAN prove the object store answered is able to say so.
	 */
	host?: "api" | "s3" | "unknown";
}

/**
 * Names and message shapes that prove a failure came from the OBJECT STORE rather than the API.
 * Typed first (`S3HttpError`, `BlobPutHttpError`, `AmbiguousUploadTargetError` are all minted by
 * this package), then the `S3 PUT <status>` sentence both PUT transports author themselves.
 */
function isObjectStoreShapedFailure(marker: string, details: ErrorLike): boolean {
	const name = typeof details.name === "string" ? details.name.toUpperCase() : "";
	if (name === "S3HTTPERROR" || name === "BLOBPUTHTTPERROR" || name === "AMBIGUOUSUPLOADTARGETERROR") return true;
	return marker.includes("S3 PUT ") || marker.includes("AMBIGUOUS_UPLOAD_TARGET");
}

/**
 * R-2: is this failure "waiting for a credential we do not currently hold"?
 *
 * Evidence tiers, strongest first — the same shape as `isOfflineUploadFailure`, and for the same
 * reason: a verdict that can be reached from free prose is a verdict a WAF error page can forge.
 *
 *  1. `host === "s3"` — decisive NEGATIVE. A presigned-URL rejection is an expiry the refresh lane
 *     in `uploadChunk.ts` already owns; conflating it with API auth is one of the four bugs R-2
 *     names. The same negative is taken from the error's own typed identity (`S3HttpError`,
 *     `BlobPutHttpError`), so a caller that cannot say which host answered is still protected.
 *  2. `authCurrent === false` — the host's own verdict. We hold no credential, so whatever the
 *     server said, the honest state is auth-gated.
 *  3. A TYPED wire code (`name`/`code`) from `AUTH_GATED_ERROR_CODES`. Typed means the failing layer set
 *     it, not that a server body happened to contain the words.
 *  4. A real HTTP status: 401, 403 or 404. The 404 is in because the server DOCUMENTS its 404 as
 *     deliberately identical for an absent id, a wrong tenant and a no-longer-permitted upload, so it
 *     cannot prove absence — and the object-store 404s that genuinely do prove it (`NoSuchUpload`, a
 *     stale presign) are already excluded by tier 1 and by the `NOSUCHUPLOAD` /
 *     `AMBIGUOUS_UPLOAD_TARGET` rules that run before this classifier's auth branch. A status is a
 *     RESPONSE — it is decisive either way, so a non-auth status stops here rather than falling
 *     through to the weaker text tier.
 *  5. A code token surviving in the MESSAGE, which is all a durable row keeps. The client-owned
 *     marker must be the formatter's trailing suffix; wire codes retain their delimiter-boundary
 *     matching anywhere in the message. Suppressed when the host says the credential is current,
 *     since then the only thing left arguing for auth is a string we did not author.
 *
 * It fails toward auth-gated on purpose. An auth-gated item stays schedulable, byte-intact and
 * budget-free, so a false positive costs one paced request; a false negative is what strands the
 * customer's only copy.
 */
export function isAuthUploadFailure(error: unknown, evidence?: UploadAuthFailureEvidence | boolean): boolean {
	if (error === undefined || error === null) return false;
	if (isUploadTerminalRefusal(error)) return !isUploadTerminalFailure(error);
	const resolved: UploadAuthFailureEvidence = typeof evidence === "boolean" ? { authCurrent: evidence } : (evidence ?? {});
	if (resolved.host === "s3") return false;
	const details = typeof error === "string" ? ({ message: error } as ErrorLike) : asErrorLike(error);
	const marker = getMarker(details);
	if (isObjectStoreShapedFailure(marker, details)) return false;
	if (resolved.authCurrent === false) return true;
	const typedMarker = [details.name, details.code]
		.filter((value): value is string => typeof value === "string")
		.join(" ")
		.toUpperCase();
	if (AUTH_GATED_CODE_PATTERNS.some((pattern) => pattern.test(typedMarker))) return true;
	const status = getStatus(details);
	if (typeof status === "number" && status >= 100 && status <= 599) {
		return status === 401 || status === 403 || status === 404;
	}
	if (resolved.authCurrent === true) return false;
	return hasPersistedAuthGatedMessage(details);
}

/**
 * Classify common browser, HTTP, and upload-service failures. Unknown errors
 * get only a small diagnostic budget; callers must pass that attempt count so
 * an opaque failure cannot become an infinite transient loop.
 */
export function classifyUploadFailure(
	error: unknown,
	diagnosticAttempt = 0,
	authEvidence?: UploadAuthFailureEvidence
): UploadFailureClassification {
	if (isUploadTerminalRefusal(error)) {
		return isUploadTerminalFailure(error)
			? { kind: "terminal", retryable: false, terminal: true }
			: { kind: "auth", retryable: true, terminal: false, slowTier: true, costsNoAttempt: true };
	}
	const details = asErrorLike(error);
	const marker = getMarker(details);
	const status = getStatus(details);

	if (details.cancelled === true || marker.includes("ABORTERROR") || marker.includes("CANCEL")) {
		return { kind: "cancelled", retryable: false, terminal: true };
	}
	// A strict parser deliberately leaves malformed NoSuchUpload payloads as ordinary HTTP
	// failures. Preserve the existing bounded retry behavior for that diagnostic shape; the
	// authoritative MultipartUploadGoneError path never reaches this classifier.
	if (marker.includes("NOSUCHUPLOAD")) {
		return { kind: "transient", retryable: true, terminal: false };
	}
	// PUT-side target ambiguity is recoverable and must not inherit the generic 4xx terminal rule.
	if (marker.includes("AMBIGUOUS_UPLOAD_TARGET")) {
		return { kind: "refresh", retryable: true, terminal: false };
	}
	if (
		marker.includes("EXPIREDTOKEN") ||
		marker.includes("TARGET_EXPIRED") ||
		marker.includes("URL_EXPIRED") ||
		marker.includes("PRESIGN_EXPIRED")
	) {
		return { kind: "refresh", retryable: true, terminal: false };
	}
	// The API uses this typed 409 only while a successfully accepted upload operation is settling.
	// Refresh immediately so callers can observe its eventual receipt without spending a backoff
	// window; unlike REPLACEMENT_IN_PROGRESS below, it is not evidence of a conflicting edit.
	if (hasTypedCode(details, "UPLOAD_OPERATION_IN_PROGRESS")) {
		return { kind: "refresh", retryable: true, terminal: false };
	}
	// The CEILING on the rule directly above. `refresh` retries forever by design, so a server that
	// keeps answering "settling" wedges a durable row into an unbounded optimistic loop. A lane that
	// has watched its own operation stay in that window past any plausible settle time mints this
	// code, and it is EVIDENCE — the elapsed observation, not an attempt count — so unlike R-0's
	// abolished exhaustion terminals it is a legitimate verdict rather than "we gave up counting".
	if (hasTypedCode(details, UPLOAD_SETTLING_EXPIRED_CODE)) {
		return { kind: "terminal", retryable: false, terminal: true };
	}
	// A previous-edit conflict is a typed rotation verdict, not the generic settling 409
	// that remains retryable below.
	if (hasTypedCode(details, "REPLACEMENT_IN_PROGRESS")) {
		return { kind: "terminal", retryable: false, terminal: true };
	}
	// LSP-19514 follow-up: a reused idempotency key with a different immutable intent. Permanent —
	// the server's `errorCode` is positive evidence, so this is NOT the status arithmetic R-2 forbids.
	if (hasTypedCode(details, "UPLOAD_INTENT_CONFLICT")) {
		return { kind: "terminal", retryable: false, terminal: true };
	}
	// Prose is fallback evidence only. A server/proxy message may mention a timeout while carrying an
	// authoritative typed verdict above; allowing that text to win would demote a permanent conflict.
	if (marker.includes("TIMEOUT") || marker.includes("ETIMEDOUT")) {
		return { kind: "timeout", retryable: true, terminal: false };
	}
	if (
		error instanceof TypeError ||
		marker.includes("NETWORK") ||
		marker.includes("ECONNRESET") ||
		marker.includes("ENOTFOUND") ||
		marker.includes("OFFLINE") ||
		status === 408 ||
		status === 409 ||
		status === 425 ||
		status === 429 ||
		(status !== undefined && status >= 500)
	) {
		return { kind: "transient", retryable: true, terminal: false };
	}
	// R-2 (owner directive 2026-08-27): an authentication or authorization failure is a PAUSE.
	// Placed above the deterministic-rejection rule below so a 401/403/404-as-access-proxy can never
	// reach it, and below the transient/refresh rules so a presign expiry keeps its own `refresh`
	// classification and an offline flap keeps `transient`.
	//
	// `retryable: true`, `terminal: false`, `costsNoAttempt: true`, `slowTier: true`: it retries
	// forever, it costs no budget, and it paces on the shared, at-most-30s cadence so an unobserved credential
	// recovery is still picked up even where no auth-recovery signal is wired.
	if (isAuthUploadFailure(error, authEvidence)) {
		return { kind: "auth", retryable: true, terminal: false, slowTier: true, costsNoAttempt: true };
	}
	// R-2 narrows what used to be a blanket `4xx -> terminal`. That rule was status ARITHMETIC, not a
	// classification: it swept 401, 403 and 404 into "this upload can never succeed" and is the single
	// site the directive names first. What survives is only the statuses that are DETERMINISTIC
	// rejections of these exact bytes by this exact request — re-sending them unchanged cannot help,
	// and no credential event changes the answer:
	//   - 400 the request itself is malformed/rejected on its content
	//   - 410 Gone: the server positively says this resource is gone (kept verbatim from R-0)
	//   - 413 the payload is too large for this endpoint
	//   - 422 the payload is well-formed but semantically rejected (the shape the target-envelope
	//         contract errors already use)
	// Every other 4xx — 405, 409, 415, 423, 429-adjacent vendor codes, 451, an opaque 499 — is now
	// unknown-and-retryable: the platform has no evidence that resending cannot work, and R-0's rule
	// is that absence of a classification is not evidence of permanence. 404 is deliberately absent:
	// the server documents it as conflating absence, wrong tenant and revoked access, so it is
	// auth-gated above (on an API host) or opaque-retryable here.
	if (status === 400 || status === 410 || status === 413 || status === 422) {
		return { kind: "terminal", retryable: false, terminal: true };
	}
	if (
		hasTypedCode(details, "UNSUPPORTED_MEDIA_TYPE") ||
		hasTypedCode(details, "INVALID_FILE_TYPE") ||
		hasExactMessage(details, "UNSUPPORTED MEDIA TYPE") ||
		hasExactMessage(details, "INVALID FILE TYPE")
	) {
		return { kind: "terminal", retryable: false, terminal: true };
	}
	// R-0: an opaque failure that has already burned its diagnostic budget is PACED, not parked.
	// The only terminals left are evidence-based (the deterministic rejections above, cancellation,
	// bytes-gone, UPLOAD_LOST, receipt-identity mismatch); "we still cannot name this error" is not
	// evidence, and neither is a status we have no rule for.
	if (diagnosticAttempt >= MAX_UNKNOWN_DIAGNOSTIC_ATTEMPTS) {
		return {
			kind: "unknown",
			retryable: true,
			terminal: false,
			slowTier: true
		};
	}
	return { kind: "unknown", retryable: true, terminal: false };
}

function hasOfflineMarker(marker: string): boolean {
  return (
    marker.includes("OFFLINE") ||
    marker.includes("ENOTFOUND") ||
    marker.includes("ERR_INTERNET_DISCONNECTED")
  );
}

/**
 * True when the failure means "this device currently has no network", which under R-0 must not
 * consume an attempt at all. The chunked driver treats offline as a wake-event pause with a
 * durable shared fallback probe; this is the same contract expressed as a
 * predicate so the small-file lanes can adopt it without duplicating the marker list.
 *
 * Break-it round 1 (D4): the verdict used to be text-only, so ANY error whose name/code/MESSAGE
 * contained "OFFLINE" was read as "this device has no network" — including a 5xx body authored by the
 * server ("the media bucket is offline for maintenance"). An offline-classified failure costs no
 * attempt, records no poison and schedules no timer, so one such sentence disabled R-0's pacing for
 * that item entirely and it retried at wake speed forever. Four rules now, strongest evidence first:
 *
 *  1. A real HTTP status. A response came back, so the device demonstrably reached the server; no
 *     wording inside that response can make it a connectivity failure.
 *  2. `offline === true` — the host's current connectivity hint when this request produced no response.
 *  3. The error's own `name`/`code` (a TYPED marker the failing layer set, e.g. Node/Chrome's
 *     `ENOTFOUND`) still counts even against a host that claims to be online — `navigator.onLine` is
 *     true on a captive portal, which is exactly the DNS case.
 *  4. A MESSAGE marker counts when the error is PLATFORM-authored (`fetch` rejects with a `TypeError`
 *     that never left the device, so its wording is the browser's, not a server's) or when the host
 *     verdict is unavailable. Against a wired host that says the device is online, prose from an
 *     ordinary `Error` — which is what a server body becomes — proves nothing about connectivity.
 */
export function isOfflineUploadFailure(
  error: unknown,
  offline?: boolean,
): boolean {
  const details = asErrorLike(error);
  const status = getStatus(details);
  if (typeof status === "number" && status >= 100 && status <= 599)
    return false;
  if (offline === true) return true;
  const typedMarker = [details.name, details.code]
    .filter((value): value is string => typeof value === "string")
    .join(" ")
    .toUpperCase();
  if (hasOfflineMarker(typedMarker)) return true;
  if (offline === false && !(error instanceof TypeError)) return false;
  return hasOfflineMarker(
    typeof details.message === "string" ? details.message.toUpperCase() : "",
  );
}

/** Reads the browser's own connectivity flag, defaulting to "online" where it is unavailable. */
export function isBrowserOffline(): boolean {
  const connectivity = globalThis.navigator as { onLine?: unknown } | undefined;
  return typeof connectivity?.onLine === "boolean"
    ? connectivity.onLine === false
    : false;
}

export interface RetryDecisionInput {
	error: unknown;
	attempt: number;
	maxActiveAttempts?: number;
	diagnosticAttempt?: number;
	offline?: boolean;
	/** R-2: where the failure came from, so a presign 403 is never read as an API auth rejection. */
	authEvidence?: UploadAuthFailureEvidence;
}

/** True only when the current drain pass may make another active attempt. */
export function shouldRetryUpload(input: RetryDecisionInput): boolean {
	if (input.offline === true) return false;
	const classification = classifyUploadFailure(input.error, input.diagnosticAttempt ?? 0, input.authEvidence);
	// R-2 property 1 + the companion directive ("MAX_RETRIES should not be a thing"): a budget-free
	// failure has not spent an attempt, so the attempt CEILING cannot be the thing that stops it.
	// Checked before the ceiling rather than after, because an auth-gated item that arrived here with a
	// stale count from before the classification existed must not be refused on that stale count.
	if (classification.costsNoAttempt === true) return classification.retryable;
	const maxAttempts = input.maxActiveAttempts ?? DEFAULT_MAX_ACTIVE_ATTEMPTS;
	if (!Number.isInteger(input.attempt) || input.attempt < 0 || input.attempt >= maxAttempts) return false;
	return classification.retryable;
}

export interface RetryPolicyInput {
  attempt: number;
  nowMs: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  /** A source of [0, 1] randomness, injectable for deterministic tests. */
  random?: () => number;
  offline?: boolean;
}

/**
 * Return a durable timestamp for requeueing. Offline work retains a shared
 * probe so a missed browser online event cannot park durable bytes forever.
 */
export function computeRetryAt(input: RetryPolicyInput): number | null {
  if (input.offline === true) return input.nowMs + OFFLINE_RETRY_PROBE_MS;
  const baseDelayMs = Math.min(DEFAULT_RETRY_MAX_DELAY_MS, Math.max(
    0,
    input.baseDelayMs ?? DEFAULT_RETRY_BASE_DELAY_MS,
  ));
  const maxDelayMs = Math.min(DEFAULT_RETRY_MAX_DELAY_MS, Math.max(
    baseDelayMs,
    input.maxDelayMs ?? DEFAULT_RETRY_MAX_DELAY_MS,
  ));
  // Break-it round 1 (D7): a non-finite attempt used to flow straight through `Math.floor` / `2 **`
  // and come back out as a NaN `retryAt`. `NaN <= now` is false forever, so one corrupt persisted
  // `retryAttempt` produced a row no drain would ever admit again and no status to show for it — an
  // unschedulable park, which is the exact thing R-0 abolished. Treat it as a first attempt and SAY
  // SO (root CLAUDE.md, "Diagnostic logging when fixing silent failures"): the corruption itself is
  // the finding, and it is invisible from the resulting row.
  if (!Number.isFinite(input.attempt)) {
    console.warn(
      "[Upload] Retry attempt count is not a finite number; scheduling as a first attempt",
      { attempt: input.attempt },
    );
  }
  const attempt = Number.isFinite(input.attempt)
    ? Math.max(0, Math.floor(input.attempt))
    : 0;
  const exponentialDelay = Math.min(maxDelayMs, baseDelayMs * 2 ** attempt);
  const randomValue = Math.min(
    1,
    Math.max(0, input.random?.() ?? Math.random()),
  );
  const jitteredDelay = Math.min(
    maxDelayMs,
    exponentialDelay * (0.5 + randomValue * 0.5),
  );
  // Same class as the attempt guard above: a non-finite clock reading would carry straight through
  // into the durable row and strand it. Fall back to the real clock rather than persisting NaN.
  if (!Number.isFinite(input.nowMs)) {
    console.warn(
      "[Upload] Retry scheduling clock is not a finite number; falling back to the wall clock",
      { nowMs: input.nowMs },
    );
    return Date.now() + Math.ceil(jitteredDelay);
  }
  return input.nowMs + Math.ceil(jitteredDelay);
}

/** Repeated failures stay paced, but never wait longer than 30 seconds. */
export function computeSlowTierRetryAt(input: RetryPolicyInput): number | null {
  return computeRetryAt({
    ...input,
    baseDelayMs: input.baseDelayMs ?? R0_SLOW_TIER_BASE_DELAY_MS,
    maxDelayMs: input.maxDelayMs ?? R0_SLOW_TIER_MAX_DELAY_MS,
  });
}
